/** Short, user-launched walkthrough on the real Executive Dashboard. */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const STEPS: Array<{ selector: string; title: string; body: string }> = [
  { selector: '[data-onboarding-tour="period"]', title: "Change the date range", body: "This controls the whole page, so every card stays on the same time period." },
  { selector: '[aria-label="Dollar basis"]', title: "Switch revenue and CM", body: "Use Revenue for sales and CM for what is left after the costs tied to each sale." },
  { selector: '[aria-label="Change chart type"]', title: "Choose the clearest chart", body: "Switch the chart when another view makes the pattern easier to understand." },
  { selector: '[aria-label="Explain"]', title: "Read the note", body: "The information button explains what the card means and calls out anything you should know." },
  { selector: "[data-card-anchor]", title: "Start with one useful question", body: "Open any card to dig into the pattern. Your first useful answer matters more than learning every feature at once." },
];

export function DashboardFirstInsightTour(): React.ReactElement | null {
  const [params, setParams] = useSearchParams();
  const active = params.get("tour") === "first-insight";
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!active) return;
    const found = nextAvailable(index);
    if (!found) { closeTour(setParams, params); return; }
    if (found.index !== index) { setIndex(found.index); return; }
    found.element.scrollIntoView({ block: "center", behavior: "smooth" });
    const update = (): void => setRect(found.element.getBoundingClientRect());
    const timer = window.setTimeout(update, 220);
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, index, params, setParams]);

  if (!active || !rect) return null;
  const step = STEPS[index];
  if (!step) return null;
  const pad = 7;
  const calloutWidth = Math.min(340, window.innerWidth - 24);
  const left = Math.min(window.innerWidth - calloutWidth - 12, Math.max(12, rect.left));
  const belowFits = rect.bottom + 180 < window.innerHeight;
  const top = belowFits ? rect.bottom + 14 : Math.max(12, rect.top - 170);
  const finish = (): void => closeTour(setParams, params);
  const next = (): void => {
    const found = nextAvailable(index + 1);
    if (found) setIndex(found.index);
    else finish();
  };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, pointerEvents: "none" }} aria-live="polite">
      <div style={{ position: "fixed", left: rect.left - pad, top: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, borderRadius: 10, border: "2px solid var(--accent)", boxShadow: "0 0 0 9999px rgba(10, 15, 25, .68)", pointerEvents: "none", transition: "all .16s ease" }} />
      <div style={{ position: "fixed", top, left, width: calloutWidth, boxSizing: "border-box", borderRadius: 12, background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "0 18px 50px rgba(0,0,0,.28)", padding: 15, pointerEvents: "auto" }}>
        <div style={{ color: "var(--accent)", fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase" }}>{index + 1} of {STEPS.length}</div>
        <div style={{ fontWeight: 780, fontSize: 15, marginTop: 4 }}>{step.title}</div>
        <div style={{ color: "var(--body)", fontSize: 12.5, lineHeight: 1.45, marginTop: 4 }}>{step.body}</div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 12 }}>
          <button type="button" onClick={finish} style={{ border: "none", background: "transparent", color: "var(--body)", fontSize: 12, cursor: "pointer" }}>Skip</button>
          <button type="button" onClick={next} style={{ border: "none", borderRadius: 7, background: "var(--accent)", color: "var(--accent-contrast)", padding: "7px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{index === STEPS.length - 1 ? "Finish" : "Next"}</button>
        </div>
      </div>
    </div>
  );
}

function nextAvailable(start: number): { index: number; element: Element } | null {
  for (let index = start; index < STEPS.length; index++) {
    const element = document.querySelector(STEPS[index]!.selector);
    if (element) return { index, element };
  }
  return null;
}

function closeTour(setParams: ReturnType<typeof useSearchParams>[1], params: URLSearchParams): void {
  const next = new URLSearchParams(params);
  next.delete("tour");
  setParams(next, { replace: true });
}
